import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, MessageCircle, Pencil, Plus } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { P, StatusPill, openPdf } from "@/components/studio/portalKit";
import { RegistrationForm, emptyReg, fromSubmission } from "@/components/studio/RegistrationForm";

const EDITABLE = ["Returned", "Payment Rejected"];

const fromCustomer = (c, isPlan, known = {}) => {
  const names = c.services.map((s) => s.name).join(" ").toLowerCase();
  const tint = /velotint|sun ?film|\btint/.test(names);
  const ppf = /\bppf\b|veloskin/.test(names) || !tint;
  return {
    ...emptyReg(),
    customer: { name: c.name, mobile: c.mobile, email: c.email || "", address: known.address || "" },
    vehicle: { make: c.brand || "", model: c.model || "", reg_no: c.reg_no || "", colour: known.colour || "", vin: known.vin || "" },
    on: { ppf, tint, plan: isPlan || !!c.protection_plan },
  };
};

const shareWa = (s, studio) => {
  const digits = String(s.customer.mobile || "").replace(/\D/g, "").slice(-10);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api${P}/public/docs/${s.share_token}.pdf`;
  const what = s.kind === "plan" ? "Protection Plan" : `${s.brand} warranty certificate`;
  const msg = `Hi ${s.customer.name}, your ${what}${s.certificate_no ? ` (No. ${s.certificate_no})` : ""} for ${s.vehicle.make} ${s.vehicle.model} ${s.vehicle.reg_no} is ready: ${url}\n— ${studio}`;
  window.open(`https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
};

const Row = ({ s, kind, onEdit, studio }) => (
  <Card data-testid={`reg-row-${s.id}`}>
    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-1">
        <p className="font-semibold text-brand-ink">{s.customer.name} · {s.vehicle.make} {s.vehicle.model} · {s.vehicle.reg_no}</p>
        <p className="text-xs text-slate-500">{s.brand} · {s.product} · {s.sqft} sqft{s.serial && ` · ${s.serial}`} · {fmtDate(s.created_at)}</p>
        {s.films?.length > 1 && <p className="text-xs text-slate-500">{s.films.map((f) => `${f.vlt} (${f.glass.join(", ")})`).join(" · ")}</p>}
        {s.certificate_no && <p className="text-xs font-medium text-emerald-700">No. {s.certificate_no}</p>}
        {EDITABLE.includes(s.status) && <p data-testid={`reg-reason-${s.id}`} className="text-sm text-red-600">Reason: {s.return_reason || s.reject_reason}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={s.status} testid={`reg-status-${s.id}`} />
        {EDITABLE.includes(s.status) && (
          <Button size="sm" variant="outline" data-testid={`reg-edit-${s.id}`} onClick={() => onEdit(s)}><Pencil className="mr-1 h-3.5 w-3.5" />Edit & resubmit</Button>
        )}
        {(s.status === "Registered" || s.status === "Sent") && (<>
          <Button size="sm" variant="outline" className="h-10" data-testid={`reg-pdf-${s.id}`} onClick={() => openPdf(`${P}/submissions/${s.id}/document.pdf`)}>
            <FileText className="mr-1 h-3.5 w-3.5" />{kind === "plan" ? "Plan document" : "Certificate"}
          </Button>
          <Button size="sm" className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid={`reg-wa-${s.id}`} onClick={() => shareWa(s, studio)}>
            <MessageCircle className="mr-1 h-3.5 w-3.5" />Send on WhatsApp
          </Button>
        </>)}
      </div>
    </CardContent>
  </Card>
);

export default function StudioRegistrations({ kind }) {
  const isPlan = kind === "plan";
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState({});
  const [form, setForm] = useState(null);
  const [follow, setFollow] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = () => api.get(`${P}/${isPlan ? "plans" : "warranties"}`).then(({ data }) => setRows(data));
  const fresh = () => ({ ...emptyReg(), on: { ppf: !isPlan, tint: false, plan: isPlan } });
  useEffect(() => {
    load();
    api.get(`${P}/me`).then(({ data }) => setMe(data));
    const cid = params.get("customer");
    if (cid) Promise.all([api.get(`${P}/customers`), api.get(`${P}/customers/${cid}/prefill`).catch(() => ({ data: {} }))])
      .then(([{ data }, { data: known }]) => { const c = data.find((x) => x.id === cid); if (c) setForm({ initial: fromCustomer(c, isPlan, known) }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  const done = (f) => { setForm(null); setParams({}); load(); setFollow(f); };
  const again = (k) => { setForm({ initial: { ...follow, on: { ppf: k === "ppf", tint: k === "tint", plan: k === "plan" } } }); setFollow(null); };
  const title = isPlan ? "Protection Plan Registration" : "Warranty Registration";
  return (
    <div className="space-y-6">
      <PageHead title={title} subtitle="Tick PPF, Velotint and Protection Plan to register them together — customer and vehicle entered once.">
        {!form && <Button data-testid="new-registration-btn" onClick={() => { setFollow(null); setForm({ initial: fresh() }); }} className="bg-crimson-500 hover:bg-crimson-600"><Plus className="mr-1.5 h-4 w-4" />New registration</Button>}
      </PageHead>
      {follow && !form && (
        <Card data-testid="reg-follow-up" className="border-emerald-200 bg-emerald-50">
          <CardContent className="space-y-3 p-4">
            <p className="font-semibold text-emerald-800">Submitted for {follow.customer.name} · {follow.vehicle.reg_no}. Register more for the same customer and vehicle:</p>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="follow-ppf" variant="outline" className="h-11 bg-white" onClick={() => again("ppf")}><Plus className="mr-1 h-4 w-4" />PPF Warranty</Button>
              <Button data-testid="follow-velotint" variant="outline" className="h-11 bg-white" onClick={() => again("tint")}><Plus className="mr-1 h-4 w-4" />Velotint Warranty</Button>
              <Button data-testid="follow-plan" variant="outline" className="h-11 bg-white" onClick={() => again("plan")}><Plus className="mr-1 h-4 w-4" />Protection Plan</Button>
              <Button variant="ghost" className="h-11" onClick={() => setFollow(null)}>Done</Button>
            </div>
          </CardContent>
        </Card>
      )}
      {form ? (
        <div className="space-y-3">
          <Button variant="ghost" data-testid="cancel-form-btn" onClick={() => { setForm(null); setParams({}); }}>← Back to list</Button>
          <RegistrationForm key={form.editing?.id || "new"} initial={form.initial} editing={form.editing} studioEmail={me.email || ""} onDone={done} />
        </div>
      ) : (
        <div className="space-y-3">
          {rows.length === 0 && <p className="text-sm text-slate-500">Nothing submitted yet.</p>}
          {rows.map((s) => <Row key={s.id} s={s} kind={kind} studio={me.name} onEdit={(x) => setForm({ editing: x, initial: fromSubmission(x) })} />)}
        </div>
      )}
    </div>
  );
}
